import { useState, type FormEvent } from 'react';
import { ApiError } from '../api/client';
import { useCreateClient } from '../api/queries/clients';
import { useLookups, activeOptionsFor } from '../api/queries/lookups';
import { LookupSelect } from './LookupSelect';
import { Modal } from './Modal';
import { PhoneListInput, type PhoneEntry } from './PhoneListInput';
import { stripPhoneForStorage } from '../utils/phone';
import type { ClientStatus } from '@stfors/shared-types';
import '../pages/NewClientPage.css';

/**
 * Sentinel <option> value for the "Other" row on a client <select>. A native
 * select can't hold a button, so the escape hatch has to be an option whose
 * value could never be a client id — the caller opens the modal and puts the
 * selection back.
 */
export const ADD_CLIENT_VALUE = '__add_client__';

interface AddClientModalProps {
  open: boolean;
  onClose: () => void;
  /** Called with the new client's id and name after a successful create. */
  onCreated?: (clientId: number, name: string) => void;
}

/**
 * Enough of a client to open a matter against, no more — the same POST
 * /api/clients the New Client page uses, minus the fields (tags, notes,
 * address) that are only worth filling in once you're on the client page.
 *
 * It exists so naming a client you haven't entered yet doesn't mean leaving a
 * half-filled project form; the full page is still the front door.
 */
export function AddClientModal({ open, onClose, onCreated }: AddClientModalProps): JSX.Element {
  const create = useCreateClient();
  const lookups = useLookups();
  const titleOptions = activeOptionsFor(lookups.data?.lookup_options, 'title');
  const suffixOptions = activeOptionsFor(lookups.data?.lookup_options, 'suffix');
  const phoneTypeOptions = activeOptionsFor(lookups.data?.lookup_options, 'phone_type');

  const [kind, setKind] = useState<'individual' | 'company'>('company');
  const [title, setTitle] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [suffix, setSuffix] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [email, setEmail] = useState('');
  const [phones, setPhones] = useState<PhoneEntry[]>([{ phone_type: 'Mobile', phone: '' }]);
  const [industry, setIndustry] = useState('');
  const [status, setStatus] = useState<ClientStatus>('active');
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setKind('company'); setTitle(''); setFirstName(''); setLastName(''); setSuffix('');
    setCompanyName(''); setEmail(''); setPhones([{ phone_type: 'Mobile', phone: '' }]);
    setIndustry(''); setStatus('active'); setError(null);
  };

  const close = () => { reset(); onClose(); };

  const incomplete = kind === 'individual'
    ? lastName.trim().length === 0
    : companyName.trim().length === 0;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const phonesPayload = phones
        .map((p) => ({ phone_type: p.phone_type, phone: stripPhoneForStorage(p.phone) ?? '' }))
        .filter((p) => p.phone.length === 10 && p.phone_type.length > 0);

      const res = await create.mutateAsync({
        kind,
        title: title.trim() || undefined,
        first_name: firstName.trim() || undefined,
        last_name: lastName.trim() || undefined,
        suffix: suffix.trim() || undefined,
        company_name: kind === 'company' ? companyName.trim() || undefined : undefined,
        email: email.trim() || undefined,
        phones: phonesPayload.length > 0 ? phonesPayload : undefined,
        industry: kind === 'company' ? industry.trim() || undefined : undefined,
        status,
      });
      // The server's `name` — a company client's is the organization's, and an
      // individual's is the generated display_name. Neither is the raw input.
      onCreated?.(res.client.id, res.client.name);
      reset();
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setError("You don't have permission to create clients.");
        return;
      }
      if (err instanceof ApiError && (err.status === 400 || err.status === 409)) {
        setError(err.message || 'Please check the highlighted fields.');
        return;
      }
      setError('Could not create the client. Please try again.');
    }
  };

  return (
    <Modal
      title="Add Client"
      open={open}
      onClose={close}
      busy={create.isPending}
      footer={(
        <>
          <button
            type="button"
            className="btn-charcoal-secondary"
            onClick={close}
            disabled={create.isPending}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="add-client-form"
            className="btn-cyan-primary"
            disabled={create.isPending || incomplete}
          >
            {create.isPending ? 'Creating…' : 'Create client'}
          </button>
        </>
      )}
    >
      <form id="add-client-form" className="new-client-form" onSubmit={onSubmit} noValidate>
        <div className="new-client-row new-client-kind-toggle">
          <button
            type="button"
            className={kind === 'individual' ? 'btn-cyan-primary' : 'btn-charcoal-secondary'}
            onClick={() => setKind('individual')}
          >
            Individual
          </button>
          <button
            type="button"
            className={kind === 'company' ? 'btn-cyan-primary' : 'btn-charcoal-secondary'}
            onClick={() => setKind('company')}
          >
            Company
          </button>
        </div>

        {kind === 'company' && (
          <div className="new-client-row">
            <label className="new-client-field new-client-field-full">
              <span>Company name *</span>
              <input
                type="text"
                className="form-input"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                maxLength={200}
                autoComplete="organization"
              />
            </label>
          </div>
        )}

        {kind === 'company' && (
          <span className="new-client-section-label">Primary contact (optional)</span>
        )}

        <div className="new-client-row">
          <label className="new-client-field new-client-field-narrow">
            <span>Title</span>
            <select
              className="form-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            >
              <option value="">—</option>
              {titleOptions.map((o) => (
                <option key={o.id} value={o.value}>{o.value}</option>
              ))}
            </select>
          </label>

          <label className="new-client-field">
            <span>First name</span>
            <input
              type="text"
              className="form-input"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              maxLength={100}
              autoComplete="given-name"
            />
          </label>

          <label className="new-client-field">
            <span>Last name{kind === 'individual' ? ' *' : ''}</span>
            <input
              type="text"
              className="form-input"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              maxLength={100}
              autoComplete="family-name"
            />
          </label>

          <label className="new-client-field new-client-field-narrow">
            <span>Suffix</span>
            <select
              className="form-input"
              value={suffix}
              onChange={(e) => setSuffix(e.target.value)}
            >
              <option value="">—</option>
              {suffixOptions.map((o) => (
                <option key={o.id} value={o.value}>{o.value}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="new-client-row">
          <label className="new-client-field new-client-field-full">
            <span>Email</span>
            <input
              type="email"
              className="form-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="off"
            />
          </label>
        </div>

        <div className="new-client-field new-client-field-full">
          <span className="new-client-section-label">Phone numbers</span>
          <PhoneListInput
            value={phones}
            onChange={setPhones}
            typeOptions={phoneTypeOptions}
            disabled={create.isPending}
          />
        </div>

        <div className="new-client-row">
          {kind === 'company' && (
            <label className="new-client-field">
              <span>Industry</span>
              <input
                type="text"
                className="form-input"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                maxLength={100}
              />
            </label>
          )}

          <label className="new-client-field">
            <span>Status</span>
            <LookupSelect
              category="client_status"
              value={status}
              onChange={setStatus}
              className="form-input"
              aria-label="Status"
            />
          </label>
        </div>

        <p className="new-client-section-label">
          Address, tags and notes are filled in on the client&apos;s own page.
        </p>

        {error && <p className="new-client-error" role="alert">{error}</p>}
      </form>
    </Modal>
  );
}
